import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { itineraryApi } from '../services/api';
import { Button } from './ui/button';
import {
  TrendingDown, TrendingUp, Minus, ChevronDown, ChevronUp,
  BarChart3, Loader2, Hotel, UtensilsCrossed, Car, Ticket, ShoppingBag, MoreHorizontal, Plane,
} from 'lucide-react';

const CAT_META = {
  accommodation: { icon: Hotel, color: 'bg-purple-500', light: 'bg-purple-100 text-purple-700', bar: 'bg-purple-500' },
  flights: { icon: Plane, color: 'bg-indigo-500', light: 'bg-indigo-100 text-indigo-700', bar: 'bg-indigo-500' },
  food: { icon: UtensilsCrossed, color: 'bg-orange-500', light: 'bg-orange-100 text-orange-700', bar: 'bg-orange-500' },
  transport: { icon: Car, color: 'bg-blue-500', light: 'bg-blue-100 text-blue-700', bar: 'bg-blue-500', label: 'Local Transport' },
  activities: { icon: Ticket, color: 'bg-teal-500', light: 'bg-teal-100 text-teal-700', bar: 'bg-teal-500' },
  shopping: { icon: ShoppingBag, color: 'bg-pink-500', light: 'bg-pink-100 text-pink-700', bar: 'bg-pink-500' },
  other: { icon: MoreHorizontal, color: 'bg-gray-500', light: 'bg-gray-100 text-gray-700', bar: 'bg-gray-500' },
};

const getMeta = (cat) => CAT_META[cat] || CAT_META.other;

const StatusBadge = ({ status, difference, currency }) => {
  if (status === 'under') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
      <TrendingDown className="w-3 h-3" /> {currency} {Math.abs(difference).toLocaleString()} saved
    </span>
  );
  if (status === 'over') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
      <TrendingUp className="w-3 h-3" /> {currency} {Math.abs(difference).toLocaleString()} over
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
      <Minus className="w-3 h-3" /> On budget
    </span>
  );
};

const ComparisonBar = ({ category, estimated, actual, currency }) => {
  const meta = getMeta(category);
  const Icon = meta.icon;
  const displayLabel = meta.label || category;
  const notEstimated = estimated === 0 && actual > 0;
  const maxVal = Math.max(estimated, actual, 1);
  const estWidth = Math.min((estimated / maxVal) * 100, 100);
  const actWidth = Math.min((actual / maxVal) * 100, 100);
  const isOver = actual > estimated && estimated > 0;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 ${meta.color} rounded-lg flex items-center justify-center text-white`}>
            <Icon className="w-3.5 h-3.5" />
          </div>
          <span className="text-sm font-medium text-gray-800 capitalize">{displayLabel}</span>
        </div>
        <div className="text-right text-xs text-gray-500 tabular-nums">
          <span className={isOver ? 'text-red-600 font-semibold' : 'text-gray-700 font-semibold'}>{currency} {actual.toLocaleString()}</span>
          <span className="mx-1">/</span>
          {notEstimated
            ? <span className="italic text-gray-400">Not estimated</span>
            : <span>{currency} {estimated.toLocaleString()}</span>}
        </div>
      </div>
      <div className="relative h-5 bg-gray-100 rounded-full overflow-hidden">
        {!notEstimated && (
          <div className="absolute inset-y-0 left-0 bg-gray-300/50 rounded-full transition-all duration-700" style={{ width: `${estWidth}%` }} />
        )}
        <div
          className={`absolute inset-y-0 left-0 rounded-full transition-all duration-700 ${isOver ? 'bg-red-400' : meta.bar}`}
          style={{ width: `${notEstimated ? 100 : actWidth}%`, opacity: notEstimated ? 0.4 : 0.85 }}
        />
        {estimated > 0 && (
          <div className="absolute inset-y-0 border-r-2 border-dashed border-gray-500/50" style={{ left: `${estWidth}%` }} title="Estimated" />
        )}
      </div>
    </div>
  );
};

const DailyTrend = ({ dailyTrend, dailyAvgEstimated, currency }) => {
  if (!dailyTrend || dailyTrend.length === 0) return null;
  const maxCum = Math.max(...dailyTrend.map(d => d.cumulative), 1);

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold text-gray-700">Daily Spending Trend</h4>
      <div className="flex items-end gap-1 h-28">
        {dailyTrend.map((d, i) => {
          const height = (d.daily / maxCum) * 100;
          const dateLabel = d.date.split('-').slice(1).join('/');
          return (
            <div key={d.date || `day-${i}`} className="flex-1 flex flex-col items-center gap-0.5" title={`${dateLabel}: ${currency} ${d.daily.toLocaleString()}`}>
              <span className="text-[9px] text-gray-500 tabular-nums">{currency} {d.daily.toLocaleString()}</span>
              <div className="w-full bg-gray-100 rounded-t relative" style={{ height: '80px' }}>
                <div
                  className="absolute bottom-0 left-0 right-0 bg-ocean/70 rounded-t transition-all duration-500"
                  style={{ height: `${Math.max(height, 3)}%` }}
                />
              </div>
              <span className="text-[9px] text-gray-400">{dateLabel}</span>
            </div>
          );
        })}
      </div>
      {dailyAvgEstimated > 0 && (
        <p className="text-xs text-gray-500 text-center">
          Estimated daily average: <span className="font-semibold text-gray-700">{currency} {dailyAvgEstimated.toLocaleString()}</span>
        </p>
      )}
    </div>
  );
};

export const BudgetComparisonReport = ({ itineraryId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const loadComparison = useCallback(async () => {
    setLoading(true);
    try {
      const res = await itineraryApi.getBudgetComparison(itineraryId);
      if (res.data.success) setData(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [itineraryId]);

  useEffect(() => {
    loadComparison();
  }, [loadComparison]);

  if (loading) {
    return (
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-8 shadow-lg border border-white/30 mb-8 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-ocean" />
      </div>
    );
  }

  if (!data || data.estimated_total === 0) return null;

  const { currency, estimated_total, actual_total, total_difference, total_percent_used, total_status, categories, daily_trend, daily_avg_estimated } = data;
  const hasExpenses = actual_total > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-white/30 mb-8 overflow-hidden"
      data-testid="budget-comparison-report"
    >
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-5 hover:bg-gray-50/50 transition-colors"
        data-testid="toggle-comparison-report"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-ocean/10 rounded-xl flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-ocean" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900">Budget Comparison Report</h3>
            <p className="text-xs text-gray-500">Estimated vs Actual Spending</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {hasExpenses && (
            <StatusBadge status={total_status} difference={total_difference} currency={currency} />
          )}
          {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </div>
      </button>

      {expanded && (
        <div className="px-5 pb-5 space-y-6">
          {/* Summary Banner */}
          <div className={`rounded-xl p-4 ${total_status === 'under' ? 'bg-emerald-50 border border-emerald-200' : total_status === 'over' ? 'bg-red-50 border border-red-200' : 'bg-gray-50 border border-gray-200'}`} data-testid="comparison-summary">
            {hasExpenses ? (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div>
                  <p className={`text-lg font-bold ${total_status === 'under' ? 'text-emerald-800' : total_status === 'over' ? 'text-red-800' : 'text-gray-800'}`}>
                    {total_status === 'under'
                      ? `You saved ${currency} ${Math.abs(total_difference).toLocaleString()}!`
                      : total_status === 'over'
                        ? `Overspent by ${currency} ${Math.abs(total_difference).toLocaleString()}`
                        : 'Right on budget!'}
                  </p>
                  <p className="text-sm text-gray-600 mt-0.5">
                    Spent {currency} {actual_total.toLocaleString()} of {currency} {estimated_total.toLocaleString()} estimated ({total_percent_used}%)
                  </p>
                </div>
                {/* Progress ring */}
                <div className="relative w-16 h-16 flex-shrink-0" data-testid="budget-progress-ring">
                  <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                    <circle cx="32" cy="32" r="26" fill="none" stroke="#e5e7eb" strokeWidth="6" />
                    <circle
                      cx="32" cy="32" r="26" fill="none"
                      stroke={total_status === 'over' ? '#ef4444' : '#10b981'}
                      strokeWidth="6" strokeLinecap="round"
                      strokeDasharray={`${Math.min(total_percent_used, 100) * 1.634} 163.4`}
                    />
                  </svg>
                  <span className={`absolute inset-0 flex items-center justify-center text-xs font-bold ${total_status === 'over' ? 'text-red-600' : 'text-emerald-600'}`}>
                    {Math.round(total_percent_used)}%
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-600 text-center">
                No expenses logged yet. Start tracking to see how you compare against the {currency} {estimated_total.toLocaleString()} estimated budget.
              </p>
            )}
          </div>

          {/* Category Comparison Bars */}
          <div className="space-y-4" data-testid="category-comparison-bars">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-700">Category Breakdown</h4>
              <div className="flex items-center gap-3 text-[10px] text-gray-500">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-gray-300 inline-block" /> Estimated</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-ocean inline-block" /> Actual</span>
              </div>
            </div>
            {categories.map(cat => (
              <ComparisonBar
                key={cat.category}
                category={cat.category}
                estimated={cat.estimated}
                actual={cat.actual}
                currency={currency}
              />
            ))}
          </div>

          {/* Daily Trend */}
          {hasExpenses && daily_trend.length > 0 && (
            <DailyTrend dailyTrend={daily_trend} dailyAvgEstimated={daily_avg_estimated} currency={currency} />
          )}
        </div>
      )}
    </motion.div>
  );
};
